import { useEffect, useMemo, useRef, useState } from 'react';
import { Play, Pause, Download } from 'lucide-react';
import { cn } from '@/lib/utils';

// Génère un "waveform" pseudo-aléatoire mais déterministe à partir d'un seed (id de l'audio).
// Reste rapide (32 barres) et joli — approximatif mais visuellement crédible.
function generateWaveformBars(seed, count = 32) {
    let h = 0;
    for (let i = 0; i < (seed || '').length; i++) {
        h = ((h << 5) - h) + seed.charCodeAt(i);
        h |= 0;
    }
    const bars = [];
    let x = h;
    for (let i = 0; i < count; i++) {
        // xorshift32 rapide
        x ^= x << 13; x ^= x >>> 17; x ^= x << 5;
        const r = ((x >>> 0) % 1000) / 1000; // 0..1
        // Distribution : plus doux au début/fin, plus fort au milieu (bell curve).
        const t = i / (count - 1); // 0..1
        const bell = Math.sin(Math.PI * t);
        const value = 0.25 + r * 0.55 + bell * 0.25; // 0.25..1.05
        bars.push(Math.min(1, value));
    }
    return bars;
}

const SPEEDS = [1, 1.5, 2];

const fmtTime = (s) => {
    if (!s || !isFinite(s)) return '0:00';
    const total = Math.floor(s);
    const min = Math.floor(total / 60);
    const sec = total % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
};

/**
 * Player vocal compact style WhatsApp : play/pause + waveform + durée + vitesse.
 * Le composant reçoit un `blobUrl` (déjà chargé) ou attend qu'il arrive.
 * `seed` sert à générer un waveform stable pour ce média.
 */
export function VoiceMessagePlayer({ blobUrl, seed, downloadUrl, filename, loading, onDownload, compact = false, durationHint = 0, disabled = false, autoPlay = false, testIdPrefix = 'voice' }) {
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [current, setCurrent] = useState(0);
    const [duration, setDuration] = useState(durationHint);
    const [speedIdx, setSpeedIdx] = useState(0);
    const bars = useMemo(() => generateWaveformBars(seed || 'default', 32), [seed]);

    // Sync progress avec l'élément audio
    useEffect(() => {
        const el = audioRef.current;
        if (!el) return;
        const onTime = () => setCurrent(el.currentTime);
        const onLoad = () => setDuration(Number.isFinite(el.duration) && el.duration > 0 ? el.duration : durationHint);
        setPlaying(false); setCurrent(0); onLoad();
        const onEnd = () => { setPlaying(false); setCurrent(0); el.currentTime = 0; };
        el.addEventListener('timeupdate', onTime);
        el.addEventListener('loadedmetadata', onLoad);
        el.addEventListener('durationchange', onLoad);
        el.addEventListener('ended', onEnd);
        return () => {
            el.removeEventListener('timeupdate', onTime);
            el.removeEventListener('loadedmetadata', onLoad);
            el.removeEventListener('durationchange', onLoad);
            el.removeEventListener('ended', onEnd);
            el.pause();
        };
    }, [blobUrl, durationHint]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!autoPlay || !blobUrl || !audio || disabled) return;
        let active = true;
        const begin = () => { audio.play().then(() => { if (active) setPlaying(true); }).catch(() => {}); };
        if (audio.readyState >= 2) begin();
        else audio.addEventListener('canplay', begin, { once: true });
        return () => { active = false; audio.removeEventListener('canplay', begin); };
    }, [autoPlay, blobUrl, disabled]);

    useEffect(() => {
        if (disabled) { audioRef.current?.pause(); setPlaying(false); }
    }, [disabled]);

    // Ajuste la vitesse
    useEffect(() => {
        if (audioRef.current) audioRef.current.playbackRate = SPEEDS[speedIdx];
    }, [speedIdx]);

    const toggle = () => {
        const el = audioRef.current;
        if (!el || !blobUrl || disabled) return;
        if (playing) { el.pause(); setPlaying(false); }
        else {
            // Playback optimiste : on met immédiatement l'UI en "playing" pour un feedback
            // instantané, puis on tente la lecture. Si le navigateur refuse (autoplay policy
            // p.ex.), on revient à l'état pause.
            setPlaying(true);
            const p = el.play();
            if (p && typeof p.then === 'function') {
                p.catch(() => setPlaying(false));
            }
        }
    };

    const cycleSpeed = () => setSpeedIdx((i) => (i + 1) % SPEEDS.length);

    // Clic sur waveform → seek à la position
    const onBarClick = (idx) => {
        const el = audioRef.current;
        if (!el || !Number.isFinite(duration) || duration <= 0 || disabled) return;
        const ratio = idx / bars.length;
        el.currentTime = ratio * duration;
        setCurrent(el.currentTime);
    };

    const progressRatio = duration > 0 ? current / duration : 0;
    const playedCount = Math.round(progressRatio * bars.length);

    return (
        <div
            className={cn(
                'rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 shadow-sm p-2.5 flex items-center gap-2.5',
                compact ? 'max-w-xs' : 'max-w-sm w-full',
            )}
            data-testid={`${testIdPrefix}-player`}
        >
            {/* Bouton Play / Pause */}
            <button
                type="button"
                onClick={toggle}
                disabled={!blobUrl || loading || disabled}
                className={cn(
                    'w-9 h-9 rounded-full flex items-center justify-center shrink-0 shadow-sm transition-colors',
                    playing ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-white hover:bg-slate-50 text-emerald-600 border border-emerald-200',
                    (!blobUrl || loading) && 'opacity-70 cursor-wait',
                )}
                data-testid={`${testIdPrefix}-play-btn`}
                aria-label={playing ? 'Pause' : 'Lecture'}
            >
                {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
            </button>

            {/* Waveform + durée */}
            <div className="flex-1 min-w-0">
                <div className="flex items-end gap-[2px] h-7 select-none" data-testid={`${testIdPrefix}-waveform`}>
                    {bars.map((b, i) => {
                        const played = i < playedCount;
                        return (
                            <button
                                key={i}
                                type="button"
                                onClick={() => onBarClick(i)}
                                disabled={disabled || !Number.isFinite(duration) || duration <= 0}
                                data-testid={`${testIdPrefix}-seek-${i}`}
                                className={cn(
                                    'flex-1 rounded-full transition-colors',
                                    played ? 'bg-emerald-500' : 'bg-emerald-200 hover:bg-emerald-300',
                                )}
                                style={{ height: `${Math.max(15, b * 100)}%` }}
                                aria-label={`Position ${Math.round((i / bars.length) * 100)}%`}
                            />
                        );
                    })}
                </div>
                <div className="text-[10px] text-emerald-800/80 mt-0.5 flex items-center justify-between">
                    <span data-testid={`${testIdPrefix}-time`}>{fmtTime(current)} / {fmtTime(duration)}</span>
                    {filename && !compact && (
                        <span className="truncate text-emerald-700/60 ml-2" data-testid={`${testIdPrefix}-filename`}>{filename}</span>
                    )}
                </div>
            </div>

            {/* Vitesse */}
            <button
                type="button"
                onClick={cycleSpeed}
                disabled={disabled}
                className="text-[10px] font-bold rounded-full px-2 py-1 bg-white/70 hover:bg-white text-emerald-700 border border-emerald-200 shrink-0 min-w-[36px]"
                data-testid={`${testIdPrefix}-speed`}
                title="Vitesse de lecture"
                aria-label="Changer la vitesse"
            >
                {SPEEDS[speedIdx]}×
            </button>

            {/* Download optionnel */}
            {downloadUrl && onDownload && (
                <button
                    type="button"
                    onClick={onDownload}
                    className="w-7 h-7 rounded-full bg-white/70 hover:bg-white text-emerald-700 border border-emerald-200 flex items-center justify-center shrink-0"
                    title="Télécharger"
                    data-testid={`${testIdPrefix}-download`}
                >
                    <Download className="w-3.5 h-3.5" />
                </button>
            )}

            {/* Élément audio caché — preload=auto pour lecture instantanée au clic
                (les vocaux du hub sont courts, < 3 min, donc pas d'impact réseau notable) */}
            {blobUrl && (
                <audio ref={audioRef} src={blobUrl} preload="auto" className="hidden" data-testid={`${testIdPrefix}-audio`} />
            )}
        </div>
    );
}
